import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import {
    OneBillionPattern,
    OneBillionPatternAllowNegative,
    OneBillionWithTwoDecimalPattern,
    OneBillionWithTwoDecimalPatternAllowNegative,
} from '../../../core/services/constant';
import { QuestionBase } from '../../models/question/question-base';

@Component({
    selector: 'app-dynamic-form-question',
    templateUrl: './dynamic-form-question.component.html',
    styleUrls: ['./dynamic-form-question.component.scss'],
    standalone: false
})
export class DynamicFormQuestionComponent implements OnInit {
    @Input() question: QuestionBase<any>;
    @Input() form: UntypedFormGroup;
    @Input() inputClass: string;

    @Output() subFormTriggered = new EventEmitter<any>();
    private previousOptionValue: any = null;

    OneBillionWithTwoDecimalPattern = OneBillionWithTwoDecimalPattern;
    OneBillionWithTwoDecimalPatternAllowNegative = OneBillionWithTwoDecimalPatternAllowNegative;
    OneBillionPatternAllowNegative = OneBillionPatternAllowNegative;
    OneBillionPattern = OneBillionPattern;

    ngOnInit() {
        if (this.question.controlType == 'radio-boolean') {
            const currentValue = this.form.controls[this.question.key]?.value;

            if (
                (currentValue === null || currentValue === undefined || currentValue === '') &&
                this.question.value !== null &&
                this.question.value !== undefined &&
                this.question.value !== ''
            ) {
                this.form.controls[this.question.key]?.setValue(this.question.value?.toString());
            }
        }

        if (this.question.controlType == 'selection-list') {
            const value = this.question.value;

            const selectionValues = Array.isArray(value)
                ? value
                : typeof value === 'string' && value.trim() !== ''
                    ? value.split(',')
                    : [];

            this.form.controls[this.question.key].setValue(selectionValues);
            this.question.value = selectionValues;
        }

    if (
        this.question.controlType == 'datepicker' &&
        this.question.isToday &&
        !this.question.value
    ) {
        this.form.controls[this.question.key].setValue(moment());
    }

    if (this.question.readonly) {
        this.form.controls[this.question.key]?.disable();
    }

    const controlValue = this.form?.get(this.question.key)?.value;

    this.previousOptionValue =
        controlValue !== null && controlValue !== undefined && controlValue !== ''
            ? controlValue
            : this.question.value;
    }

    onOptionChanged(selectedValue: any): void {
        const questionAny = this.question as any;

        const selectedValues = Array.isArray(selectedValue)
            ? selectedValue.map((x) => x?.toString())
            : [selectedValue?.toString()];

        const selectedOption = this.question?.options?.find((option: any) =>
            selectedValues.includes(option.key?.toString()) ||
            selectedValues.includes(option.value?.toString())
        );

        const selectedOptionAny = selectedOption as any;

        const subForm =
            selectedOptionAny?.subForm ||
            questionAny?.subForm ||
            null;

        const subFormId =
            selectedOptionAny?.subFormId ||
            selectedOptionAny?.subformId ||
            selectedOptionAny?.dynamicSubFormId ||
            selectedOptionAny?.dynamicFormSubFormId ||
            selectedOptionAny?.subForm?.id ||
            selectedOptionAny?.subForm?.dynamicFormTemplateId ||
            questionAny?.subFormId ||
            questionAny?.subformId ||
            questionAny?.dynamicSubFormId ||
            questionAny?.dynamicFormSubFormId ||
            questionAny?.subForm?.id ||
            questionAny?.subForm?.dynamicFormTemplateId ||
            null;

        // Debug logging removed to avoid leaking form metadata into the browser console.

        const previousValue = this.previousOptionValue;

        this.subFormTriggered.emit({
            question: this.question,
            selectedValue,
            previousValue,
            selectedOption,
            subForm,
            subFormId,
            confirmOptionChange: () => {
                this.previousOptionValue = selectedValue;
                this.question.value = selectedValue?.toString();
            },
            revertOptionChange: () => {
                this.form?.get(this.question.key)?.setValue(previousValue, {
                    emitEvent: false,
                });

                this.question.value = previousValue?.toString();
                this.previousOptionValue = previousValue;
            },
        });
    }

    onSelectionListChanged(): void {
        setTimeout(() => {
            const rawValue = this.form?.get(this.question.key)?.value ?? [];
            const currentValues = Array.isArray(rawValue) ? rawValue.map((v: any) => v?.toString()) : [];
            const previousValues = Array.isArray(this.previousOptionValue)
                ? this.previousOptionValue.map((v: any) => v?.toString())
                : [];

            const newlySelected = currentValues.filter((v: string) => !previousValues.includes(v));
            const deselected = previousValues.filter((v: string) => !currentValues.includes(v));

            [...newlySelected, ...deselected].forEach((value: string) => {
                const isRemoval = deselected.includes(value);

                const option = this.question?.options?.find(
                    (opt: any) => opt.key?.toString() === value || opt.value?.toString() === value,
                );

                if (!option) return;

                const optionAny = option as any;
                const subForm = optionAny?.subForm || null;
                const subFormId =
                    optionAny?.subFormId ||
                    optionAny?.subformId ||
                    optionAny?.dynamicSubFormId ||
                    optionAny?.dynamicFormSubFormId ||
                    optionAny?.subForm?.id ||
                    optionAny?.subForm?.dynamicFormTemplateId ||
                    null;

                if (isRemoval && !subFormId) return;
                if (!isRemoval && !subForm && !subFormId) return;

                this.subFormTriggered.emit({
                    question: this.question,
                    selectedValue: currentValues,
                    previousValue: previousValues,
                    selectedOption: option,
                    subForm: isRemoval ? null : subForm,
                    subFormId: subFormId,
                    isSubFormRemoval: isRemoval,
                    confirmOptionChange: () => {},
                    revertOptionChange: () => {
                        this.form?.get(this.question.key)?.setValue(previousValues, {
                            emitEvent: false,
                        });

                        this.question.value = previousValues?.toString();
                        this.previousOptionValue = previousValues;
                    },
                    // Multi-select: a failed load must drop only this option, keeping the
                    // other selections (whose own loads may still be in flight) intact.
                    removeFailedOption: () => {
                        const control = this.form?.get(this.question.key);
                        const liveValues = Array.isArray(control?.value)
                            ? control.value.map((v: any) => v?.toString())
                            : [];
                        const remaining = liveValues.filter((v: string) => v !== value);

                        control?.setValue(remaining, { emitEvent: false });
                        this.question.value = remaining?.toString();
                        this.previousOptionValue = remaining;
                    },
                    getCurrentSelection: () => {
                        const liveValue = this.form?.get(this.question.key)?.value;
                        return Array.isArray(liveValue) ? liveValue.map((v: any) => v?.toString()) : liveValue;
                    },
                });
            });

            this.previousOptionValue = currentValues;
            this.question.value = currentValues?.toString();
        });
    }
}
